// API service for Integrable backend integration
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Import authService for token retrieval
  async getAuthTokenFromService() {
    try {
      const { authService } = await import('./authService.js');
      return authService.getToken();
    } catch (error) {
      console.error('Failed to import authService:', error);
      return null;
    }
  }

  // Debug function to check all cookies
  debugCookies() {
    const cookies = {};
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf('=') !== -1) {
        const parts = c.split('=');
        cookies[parts[0]] = parts[1];
      }
    }
    return cookies;
  }

  // Test authentication
  async testAuth() {
    try {
      await this.get('/contacts?projectId=test');
      return true;
    } catch (error) {
      console.error('API Service - Auth test failed:', error);
      return false;
    }
  }

  // Test message payload structure
  testMessagePayload() {
    const textMessagePayload = {
      contactId: 'test_contact_id',
      projectId: 'test_project_id',
      messageData: {
        type: 'text',
        text: {
          body: 'Hello Arjun',
        },
      },
    };

    const conversationMessagePayload = {
      projectId: 'test_project_id',
      messageData: {
        type: 'text',
        text: {
          body: 'Hello Arjun',
        },
      },
    };

    // Test the exact payload structure you specified
    const exactPayloadStructure = {
      contactId: '6890ee8eba03184830592b84',
      projectId: '688dd8ac130dea83e3cf6975',
      messageData: {
        type: 'text',
        text: {
          body: 'Hello Arjun',
        },
      },
    };

    return {
      textMessagePayload,
      conversationMessagePayload,
      exactPayloadStructure,
    };
  }

  // Get auth token from cookies (to match authService)
  getAuthToken() {
    const nameEQ = 'accessToken=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        const token = c.substring(nameEQ.length, c.length);
        return token;
      }
    }
    return null;
  }

  // Get user data from cookies (to match authService)
  getUser() {
    const nameEQ = 'user=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        const userData = c.substring(nameEQ.length, c.length);
        return userData ? JSON.parse(userData) : null;
      }
    }
    return null;
  }

  // Generic request method with authentication
  async request(endpoint, options = {}) {
    // Debug cookies for troubleshooting
    this.debugCookies();

    let token = this.getAuthToken();

    // Fallback to authService if no token found
    if (!token) {
      token = await this.getAuthTokenFromService();
    }

    // Check if token is about to expire (within 2 minutes) and refresh if needed
    if (token) {
      try {
        const { authService } = await import('./authService.js');
        await this.attemptTokenRefresh(authService);
        // Get the potentially refreshed token
        token = this.getAuthToken();
      } catch (error) {
        console.warn('Token refresh attempt failed:', error);
        // Don't fail the request if refresh fails, let the server handle it
      }
    }

    // Check if body is FormData - if so, don't set Content-Type (browser will handle it)
    const isFormData = options.body instanceof FormData;

    const { headers: extraHeaders, ...otherOptions } = options;

    const config = {
      ...otherOptions,
      headers: {
        // Only set Content-Type for non-FormData requests
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        ...(token && { Authorization: `Bearer ${token}` }),
        ...extraHeaders,
      },
    };

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, config);

      // Try to parse response based on responseType
      if (options.responseType === 'blob') {
        return await response.blob();
      }

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new Error('Invalid JSON response from server');
      }

      if (
        response.status === 401 &&
        !endpoint.includes('/auth/login') &&
        !endpoint.includes('/auth/register') &&
        !endpoint.includes('/auth/refresh-token')
      ) {
        console.warn(`API 401 at ${endpoint}, attempting token refresh...`);
        try {
          const { authService } = await import('./authService.js');
          await authService.refreshToken();

          // Retry the original request with the new token
          const newToken = this.getAuthToken();
          if (newToken) {
            config.headers.Authorization = `Bearer ${newToken}`;
            const retryResponse = await fetch(
              `${this.baseURL}${endpoint}`,
              config
            );

            if (retryResponse.ok) {
              const retryData = await retryResponse.json();
              return retryData;
            } else if (retryResponse.status === 401) {
              // If retry also fails with 401, logout
              authService.logout();
              throw new Error('Session expired. Please login again.');
            }
          }
        } catch (refreshError) {
          console.error('Token refresh or retry failed:', refreshError);
          // authService.refreshToken already handles logout for most cases,
          // but we throw here to notify the caller
          throw new Error('Authentication failed. Please login again.');
        }
      }

      if (!response.ok) {
        console.error('API request failed:', {
          status: response.status,
          statusText: response.statusText,
          data: data,
        });
        const errorMessage =
          data?.message ||
          data?.error ||
          `HTTP ${response.status}: ${response.statusText}`;
        const error = new Error(errorMessage);
        error.response = { status: response.status, data: data || {} };
        throw error;
      }

      if (data && data.success === false) {
        const errorMessage = data.message || data.error || 'Request failed';
        const error = new Error(errorMessage);
        error.response = { status: 400, data };
        throw error;
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Attempt to refresh token if it's about to expire
  async attemptTokenRefresh(authService) {
    try {
      const token = this.getAuthToken();
      if (!token) return;

      // Decode JWT token to check expiration (without external library)
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) return;

      const payload = JSON.parse(atob(tokenParts[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = payload.exp - currentTime;

      // If token expires within 30 minutes, refresh it (more conservative)
      if (timeUntilExpiry < 900) {
        await authService.refreshToken();
      }
    } catch (error) {
      console.warn('Error checking token expiry:', error);
    }
  }

  // Generic HTTP methods
  async get(endpoint, options = {}) {
    return await this.request(endpoint, { method: 'GET', ...options });
  }

  async post(endpoint, data, options = {}) {
    // Check if data is FormData - don't stringify it
    const isFormData = data instanceof FormData;

    // Extract headers from options to avoid overwriting
    const { headers: optionsHeaders, ...restOptions } = options;

    return await this.request(endpoint, {
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
      ...restOptions,
      // Pass headers separately so they can be merged properly in request()
      ...(optionsHeaders && { headers: optionsHeaders }),
    });
  }

  async put(endpoint, data, options = {}) {
    return await this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options,
    });
  }

  async delete(endpoint, options = {}) {
    return await this.request(endpoint, { method: 'DELETE', ...options });
  }

  async patch(endpoint, data, options = {}) {
    return await this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
      ...options,
    });
  }

  // Authentication
  async login(email, password) {
    const response = await this.post('/auth/login', { email, password });

    if (response.success) {
      // Store tokens in cookies (to match authService)
      document.cookie = `accessToken=${response.data.accessToken};path=/;SameSite=Strict`;
      document.cookie = `refreshToken=${response.data.refreshToken};path=/;SameSite=Strict`;
      document.cookie = `user=${JSON.stringify(response.data.user)};path=/;SameSite=Strict`;
    }

    return response;
  }

  async logout() {
    // Clear cookies (to match authService)
    document.cookie =
      'accessToken=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
    document.cookie =
      'refreshToken=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
    document.cookie = 'user=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
  }

  // WhatsApp Configuration
  async configureWhatsApp(projectId, config) {
    return await this.post(`/whatsapp/${projectId}/configure`, config);
  }

  async getWhatsAppConfig(projectId) {
    return await this.get(`/whatsapp/${projectId}/config`);
  }

  async updateWhatsAppConfig(projectId, config) {
    return await this.put(`/whatsapp/${projectId}/config`, config);
  }

  async testWhatsAppConnection(projectId) {
    return await this.post(`/whatsapp/${projectId}/test-connection`);
  }

  async sendTestMessage(projectId, phoneNumber, message) {
    return await this.post(`/whatsapp/${projectId}/test-message`, {
      phoneNumber,
      message,
    });
  }

  async deactivateWhatsApp(projectId) {
    return await this.put(`/whatsapp/${projectId}/deactivate`);
  }

  // Conversations
  async getConversations(projectId, filters = {}) {
    // Filter out empty values
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(
        ([, value]) => value !== null && value !== undefined && value !== ''
      )
    );
    const params = new URLSearchParams({ projectId, ...cleanFilters });
    return await this.get(`/conversations?${params}`);
  }

  // Start new conversation with a contact
  async startConversation(projectId, contactId) {
    return await this.post('/conversations/start', {
      projectId,
      contactId,
    });
  }

  // Initiate conversation (find or create) for a contact
  async initiateConversation(contactId, projectId) {
    return await this.post(`/contacts/${contactId}/conversation`, {
      projectId,
    });
  }

  async getConversation(projectId, conversationId) {
    const params = new URLSearchParams({ projectId });
    return await this.get(`/conversations/${conversationId}?${params}`);
  }

  async getConversationForContact(projectId, contactId) {
    const params = new URLSearchParams({ projectId });
    return await this.get(`/conversations?${params}&contactId=${contactId}`);
  }

  async createConversation(projectId, contactId) {
    return await this.post(`/conversations`, {
      projectId,
      contactId,
    });
  }

  async getConversationMessages(projectId, conversationId, filters = {}) {
    // Filter out empty values
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(
        ([, value]) => value !== null && value !== undefined && value !== ''
      )
    );
    const params = new URLSearchParams({ projectId, ...cleanFilters });
    return await this.get(
      `/conversations/${conversationId}/messages?${params}`
    );
  }

  async sendMessage(projectId, conversationId, messageData) {
    // Format payload with messageData object containing WhatsApp Cloud API structure
    const payload = {
      projectId,
      messageData: {
        // Keep the WhatsApp Cloud API structure in messageData
        ...messageData,
      },
    };

    // Add optional fields
    if (messageData.replyToMessageId) {
      payload.replyToMessageId = messageData.replyToMessageId;
    }
    if (messageData.metadata) {
      payload.metadata = messageData.metadata;
    }

    return await this.post(
      `/conversations/${conversationId}/messages`,
      payload
    );
  }

  async sendMessageToContact(projectId, contactId, messageData) {
    // Format payload with messageData object containing WhatsApp Cloud API structure
    // Reference: https://developers.facebook.com/docs/whatsapp/cloud-api/guides/send-messages
    const payload = {
      contactId,
      projectId,
      messageData: {
        // Keep the WhatsApp Cloud API structure in messageData
        ...messageData,
      },
    };

    return await this.post(`/contacts/${contactId}/send-message`, payload);
  }

  async sendTemplateMessage(
    projectId,
    conversationId,
    templateId,
    dynamicValues
  ) {
    const payload = {
      projectId,
      template: {
        id: templateId,
        dynamicValues,
      },
    };
    return await this.post(
      `/conversations/${conversationId}/messages/template`,
      payload
    );
  }

  // Helper methods for different message types
  async sendTextMessageToContact(projectId, contactId, text, options = {}) {
    return await this.sendMessageToContact(projectId, contactId, {
      type: 'text',
      text: {
        body: text,
        preview_url: options.preview_url || false,
      },
    });
  }

  async sendImageMessageToContact(projectId, contactId, imageData) {
    return await this.sendMessageToContact(projectId, contactId, {
      type: 'image',
      image: {
        id: imageData.id,
        link: imageData.link,
        caption: imageData.caption,
      },
    });
  }

  async sendDocumentMessageToContact(projectId, contactId, documentData) {
    return await this.sendMessageToContact(projectId, contactId, {
      type: 'document',
      document: {
        id: documentData.id,
        link: documentData.link,
        caption: documentData.caption,
        filename: documentData.filename,
      },
    });
  }

  async sendLocationMessageToContact(projectId, contactId, locationData) {
    return await this.sendMessageToContact(projectId, contactId, {
      type: 'location',
      location: {
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        name: locationData.name,
        address: locationData.address,
      },
    });
  }

  async sendInteractiveMessageToContact(projectId, contactId, interactiveData) {
    return await this.sendMessageToContact(projectId, contactId, {
      type: 'interactive',
      interactive: interactiveData,
    });
  }

  async sendTemplateMessageToContact(
    projectId,
    contactId,
    templateId,
    dynamicValues
  ) {
    return await this.sendMessageToContact(projectId, contactId, {
      type: 'template',
      template: {
        id: templateId,
        dynamicValues,
      },
    });
  }

  async updateConversation(projectId, conversationId, updateData) {
    return await this.put(`/conversations/${conversationId}`, {
      projectId,
      ...updateData,
    });
  }

  async markConversationAsRead(projectId, conversationId) {
    return await this.put(`/conversations/${conversationId}/read`, {
      projectId,
    });
  }

  async assignAgentToConversation(projectId, conversationId, agentId) {
    return await this.put(`/conversations/${conversationId}/assign`, {
      projectId,
      agentId,
    });
  }

  async archiveConversation(projectId, conversationId) {
    return await this.put(`/conversations/${conversationId}/archive`, {
      projectId,
    });
  }

  async unarchiveConversation(projectId, conversationId) {
    return await this.put(`/conversations/${conversationId}/unarchive`, {
      projectId,
    });
  }

  async getConversationStats(projectId) {
    const params = new URLSearchParams({ projectId });
    return await this.get(`/conversations/stats?${params}`);
  }

  // Contacts
  async getContacts(projectId, filters = {}) {
    // Filter out empty values
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(
        ([, value]) => value !== null && value !== undefined && value !== ''
      )
    );
    const params = new URLSearchParams({ projectId, ...cleanFilters });
    return await this.get(`/contacts?${params}`);
  }

  async getContact(projectId, contactId) {
    const params = new URLSearchParams({ projectId });
    return await this.get(`/contacts/${contactId}?${params}`);
  }

  async createContact(projectId, contactData) {
    return await this.post(`/contacts`, {
      projectId,
      ...contactData,
    });
  }

  async updateContact(projectId, contactId, contactData) {
    return await this.put(`/contacts/${contactId}`, {
      projectId,
      ...contactData,
    });
  }

  async deleteContact(projectId, contactId) {
    const params = new URLSearchParams({ projectId });
    return await this.delete(`/contacts/${contactId}?${params}`);
  }

  async bulkUploadContacts(projectId, file) {
    const formData = new FormData();
    formData.append('file', file);

    // For FormData, we need to let the browser set the Content-Type header
    const token = this.getAuthToken();
    const config = {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        // Don't set Content-Type for FormData - browser will set it with boundary
      },
      body: formData,
    };

    try {
      const response = await fetch(
        `${this.baseURL}/contacts/${projectId}/bulk-upload`,
        config
      );

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new Error('Invalid JSON response from server');
      }

      if (!response.ok) {
        console.error('API request failed:', {
          status: response.status,
          statusText: response.statusText,
          data: data,
        });
        throw new Error(
          data.message ||
            data.error ||
            `HTTP ${response.status}: ${response.statusText}`
        );
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }
}

// Create singleton instance
const apiService = new ApiService();

// Export both default and named export for backward compatibility
export default apiService;
export { apiService };
